import React, { useState, useEffect, useMemo } from 'react';
import { useCortes } from '../../hooks/useCortes';
import { useDistribucion } from '../../hooks/useDistribucion';
import { ConfirmModal } from '../common/ConfirmModal';
import { MenuAcciones } from '../common/MenuAcciones';
import { formatCurrency } from '../../utils/formatters';
import { nombreMes } from '../../utils/periodos';

// ¿La regla aplica al recurso? (mismo criterio que el servidor)
const aplica = (regla, service, instance) => {
  if (regla.servicio && regla.servicio !== service) return false;
  const inst = (instance || '').toLowerCase();
  const pat = (regla.patron || '').trim().toLowerCase();
  if (!pat) return false;
  if (regla.tipo === 'exacto') return inst === pat;
  if (regla.tipo === 'empieza') return inst.startsWith(pat);
  return inst.includes(pat);
};

// Tipos de regla, en el orden del selector.
const TIPOS = [
  { id: 'exacto', label: 'Este recurso', corto: 'Exacto', ayuda: 'Solo este recurso, con su nombre exacto.' },
  { id: 'empieza', label: 'Empieza por', corto: 'Empieza por', ayuda: 'Los recursos cuyo nombre empieza por el texto.' },
  { id: 'contiene', label: 'Contiene', corto: 'Contiene', ayuda: 'Los recursos que tienen el texto en cualquier parte del nombre.' }
];
const tipoDe = (id) => TIPOS.find((t) => t.id === id) || TIPOS[0];

// Nombre del recurso con la parte que coincide resaltada.
const Resaltado = ({ texto, patron, tipo }) => {
  const pat = patron.trim().toLowerCase();
  const i = pat ? (tipo === 'empieza' ? 0 : texto.toLowerCase().indexOf(pat)) : -1;
  if (i < 0 || tipo === 'exacto') return texto;
  return (
    <>
      {texto.slice(0, i)}
      <mark>{texto.slice(i, i + pat.length)}</mark>
      {texto.slice(i + pat.length)}
    </>
  );
};

// Sugerencia de texto para una regla por patrón: quita el sufijo variable
// (…-workers-0000153c → …-workers), que es lo que cambia mes a mes.
const sugerirPatron = (instance) => {
  const partes = instance.split('-');
  const ultima = partes[partes.length - 1];
  if (partes.length > 1 && /\d/.test(ultima) && ultima.length <= 12) return partes.slice(0, -1).join('-');
  return instance;
};

const describirRegla = (r) =>
  `${r.tipo === 'exacto' ? 'Recurso' : tipoDe(r.tipo).label} "${r.patron}"${r.servicio ? ` · solo ${r.servicio}` : ''}`;

/** Chips con las aplicaciones de una regla. */
const AppsDeRegla = ({ regla, apps }) => {
  if (regla.todas) return <span className="dist-chip dist-chip-todas">Todas las aplicaciones</span>;
  const nombre = (k) => apps.find((a) => a.key === k)?.name || k;
  return regla.apps.map((a) => (
    <span key={a.key} className="dist-chip">
      {nombre(a.key)}{a.pct !== null && a.pct !== undefined ? ` ${a.pct}%` : ''}
    </span>
  ));
};

/**
 * Modal para asignar un recurso: crea o edita la regla. Si el recurso ya
 * estaba cubierto por una regla, se edita esa (afecta a todos sus recursos)
 * o se crea una excepción exacta solo para él.
 */
const ReglaModal = ({ recurso, regla, apps, recursosMes, onClose, onSave, onDelete }) => {
  const [editandoId, setEditandoId] = useState(regla?.id || null);
  const [tipo, setTipo] = useState(regla?.tipo || 'exacto');
  const [patron, setPatron] = useState(regla?.patron || recurso?.instance || '');
  const [soloServicio, setSoloServicio] = useState(Boolean(regla?.servicio));
  const servicio = regla?.servicio || recurso?.service || '';
  const [todas, setTodas] = useState(Boolean(regla?.todas));
  const [sel, setSel] = useState(() => new Set((regla?.apps || []).map((a) => a.key)));
  const [usarPct, setUsarPct] = useState(Boolean(regla?.apps?.some((a) => a.pct !== null && a.pct !== undefined)));
  const [pcts, setPcts] = useState(() =>
    Object.fromEntries((regla?.apps || []).map((a) => [a.key, a.pct ?? '']))
  );
  const [enviando, setEnviando] = useState(false);

  const borrador = { tipo, patron, servicio: soloServicio ? servicio : null };
  const coinciden = recursosMes.filter((r) => aplica(borrador, r.service, r.instance));
  const seleccion = apps.filter((a) => sel.has(a.key));
  const sumaPct = seleccion.reduce((s, a) => s + (parseFloat(pcts[a.key]) || 0), 0);
  const pctOk = !usarPct || (seleccion.every((a) => parseFloat(pcts[a.key]) > 0) && Math.abs(sumaPct - 100) < 0.01);
  const patronOk = patron.trim().length > 0 && (tipo === 'exacto' || patron.trim().length >= 3);
  const valido = patronOk && (todas || seleccion.length > 0) && pctOk;

  // Con "Todas" marcada, todas las casillas se ven marcadas; desmarcar una
  // pasa a una selección explícita de todas menos esa.
  const marcada = (key) => todas || sel.has(key);
  const toggleApp = (key) => {
    if (todas) {
      setTodas(false);
      setSel(new Set(apps.map((a) => a.key).filter((k) => k !== key)));
      return;
    }
    setSel((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };
  const toggleTodas = () => {
    if (todas) {
      setTodas(false);
      setSel(new Set());
    } else {
      setTodas(true);
      setUsarPct(false);
    }
  };
  const parcial = !todas && sel.size > 0 && sel.size < apps.length;

  const cambiarTipo = (t) => {
    if (t === tipo) return;
    setTipo(t);
    if (!recurso) return;
    if (t === 'exacto') setPatron(recurso.instance);
    else if (tipo === 'exacto') setPatron(sugerirPatron(recurso.instance));
  };

  // Al activar porcentajes, arranca con partes iguales.
  const activarPct = (on) => {
    setUsarPct(on);
    if (on && seleccion.length) {
      const igual = Math.floor((100 / seleccion.length) * 100) / 100;
      const resto = Math.round((100 - igual * seleccion.length) * 100) / 100;
      setPcts(Object.fromEntries(seleccion.map((a, i) => [a.key, i === 0 ? igual + resto : igual])));
    }
  };

  const crearExcepcion = () => {
    setEditandoId(null);
    setTipo('exacto');
    setPatron(recurso.instance);
    setSoloServicio(false);
  };

  const enviar = async () => {
    setEnviando(true);
    const ok = await onSave(editandoId, {
      tipo,
      patron: patron.trim(),
      servicio: soloServicio ? servicio : null,
      todas,
      apps: todas ? [] : seleccion.map((a) => ({ key: a.key, pct: usarPct ? parseFloat(pcts[a.key]) : null }))
    });
    setEnviando(false);
    if (ok) onClose();
  };

  return (
    <div className="modal-overlay" onClick={enviando ? undefined : onClose}>
      <div className="modal-content dist-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-header-text">
            <h3 className="modal-title">{editandoId ? 'Editar asignación' : 'Asignar a aplicaciones'}</h3>
            {recurso && (
              <p className="dist-modal-recurso">
                {recurso.instance} · {recurso.service} · {formatCurrency(recurso.costo_usd)}
              </p>
            )}
          </div>
          <button className="modal-close" onClick={onClose} aria-label="Cerrar" disabled={enviando}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="modal-body consumo-form">
          {editandoId && recurso && regla?.id === editandoId && regla.tipo !== 'exacto' && (
            <div className="dist-aviso">
              Este recurso está asignado por la regla <strong>{describirRegla(regla)}</strong>. Los cambios
              afectan a todos los recursos que cubre.{' '}
              <button type="button" className="contrato-link-btn" onClick={crearExcepcion}>
                Crear una excepción solo para este recurso
              </button>
            </div>
          )}

          <div className="consumo-campo">
            <span>Aplicar a</span>
            <div className="dist-modo" role="radiogroup" aria-label="Aplicar a">
              {TIPOS.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  role="radio"
                  aria-checked={tipo === t.id}
                  className={`dist-modo-opt ${tipo === t.id ? 'activo' : ''}`}
                  onClick={() => cambiarTipo(t.id)}
                >
                  {t.label}
                </button>
              ))}
            </div>
            <small className="dist-modo-ayuda">{tipoDe(tipo).ayuda}</small>

            {tipo === 'exacto' && recurso ? (
              <div className="dist-recurso-fijo" title={recurso.instance}>{recurso.instance}</div>
            ) : (
              <div className="dist-patron">
                <span className="dist-patron-pre">{tipo === 'exacto' ? 'Recurso' : tipo === 'empieza' ? 'Empieza por' : 'Contiene'}</span>
                <input
                  value={patron}
                  onChange={(e) => setPatron(e.target.value)}
                  placeholder={tipo === 'empieza' ? 'Ej. srv-aval' : tipo === 'contiene' ? 'Ej. clustertls' : 'Nombre del recurso'}
                  autoFocus
                />
              </div>
            )}

            {servicio && (
              <label className="dist-switch">
                <input type="checkbox" checked={soloServicio} onChange={(e) => setSoloServicio(e.target.checked)} />
                <span className="dist-switch-track" aria-hidden="true" />
                Limitar al servicio <strong>{servicio}</strong>
              </label>
            )}

            <div className="dist-coinciden">
              <div className="dist-coinciden-head">
                <span className={`dist-coinciden-num ${coinciden.length ? '' : 'cero'}`}>{coinciden.length}</span>
                <span>
                  {coinciden.length === 1 ? 'recurso coincide' : 'recursos coinciden'} en este mes
                  {tipo !== 'exacto' && <small> · y los que lleguen en meses futuros</small>}
                </span>
              </div>
              {tipo !== 'exacto' && coinciden.length > 0 && (
                <ul className="dist-preview">
                  {coinciden.slice(0, 8).map((r) => (
                    <li key={`${r.service}|${r.instance}`}>
                      <span className="dist-preview-nombre">
                        <Resaltado texto={r.instance} patron={patron} tipo={tipo} />
                      </span>
                      <span className="dist-preview-usd">{formatCurrency(r.costo_usd)}</span>
                    </li>
                  ))}
                  {coinciden.length > 8 && <li className="dist-preview-mas">y {coinciden.length - 8} más</li>}
                </ul>
              )}
            </div>
          </div>

          <div className="consumo-campo">
            <span>Aplicaciones</span>
            <label className={`dist-todas ${todas ? 'on' : ''}`}>
              <input
                type="checkbox"
                checked={todas}
                ref={(el) => { if (el) el.indeterminate = parcial; }}
                onChange={toggleTodas}
              />
              <span>
                Todas las aplicaciones
                <small>
                  {todas
                    ? 'Incluye las que se agreguen después. Desmarca una para excluirla.'
                    : 'Se reparte entre todas, incluidas las que se agreguen después'}
                </small>
              </span>
            </label>
            <div className="dist-apps">
              {apps.map((a) => (
                <label key={a.key} className={`dist-app ${marcada(a.key) ? 'on' : ''}`}>
                  <input type="checkbox" checked={marcada(a.key)} onChange={() => toggleApp(a.key)} />
                  <span className="dist-app-nombre" title={a.herramientas.join(' · ')}>{a.name}</span>
                  {usarPct && !todas && sel.has(a.key) && (
                    <span className="dist-app-pct">
                      <input
                        type="number"
                        min="0"
                        max="100"
                        step="0.01"
                        value={pcts[a.key] ?? ''}
                        onClick={(e) => e.preventDefault()}
                        onChange={(e) => setPcts((p) => ({ ...p, [a.key]: e.target.value }))}
                      />
                      %
                    </span>
                  )}
                </label>
              ))}
            </div>
            {!todas && seleccion.length > 1 && (
              <div className="dist-reparto">
                <label className="dist-check">
                  <input type="checkbox" checked={usarPct} onChange={(e) => activarPct(e.target.checked)} />
                  Repartir con porcentajes
                </label>
                <small className={`consumo-ayuda ${pctOk ? '' : 'dist-error'}`}>
                  {usarPct ? `Suma: ${Math.round(sumaPct * 100) / 100}% (debe ser 100%)` : `Partes iguales: ${Math.round((100 / seleccion.length) * 100) / 100}% cada una`}
                </small>
              </div>
            )}
          </div>
        </div>

        <div className="modal-footer">
          {editandoId && (
            <button type="button" className="modal-btn modal-btn-ghost dist-quitar" onClick={() => onDelete(regla)} disabled={enviando}>
              Quitar asignación
            </button>
          )}
          <button className="modal-btn modal-btn-ghost" onClick={onClose} disabled={enviando}>Cancelar</button>
          <button className="modal-btn modal-btn-primary" disabled={!valido || enviando} onClick={enviar}>
            {enviando ? 'Guardando…' : 'Guardar'}
          </button>
        </div>
      </div>
    </div>
  );
};

const UMBRALES = [5, 10, 20];

/**
 * Asignación masiva: los recursos sin asignar de menos de X dólares van a
 * todas las aplicaciones (una regla exacta por recurso).
 */
const LoteModal = ({ recursos, onClose, onSubmit }) => {
  const [umbral, setUmbral] = useState('20');
  const [excluidos, setExcluidos] = useState(() => new Set());
  const [enviando, setEnviando] = useState(false);

  const limite = parseFloat(umbral);
  const candidatos = limite > 0
    ? recursos.filter((r) => !r.regla_id && r.costo_usd < limite).sort((a, b) => b.costo_usd - a.costo_usd)
    : [];
  const elegidos = candidatos.filter((r) => !excluidos.has(r.instance));
  const totalUsd = elegidos.reduce((s, r) => s + r.costo_usd, 0);
  const todosMarcados = candidatos.length > 0 && elegidos.length === candidatos.length;

  const toggle = (inst) => setExcluidos((prev) => {
    const next = new Set(prev);
    if (next.has(inst)) next.delete(inst);
    else next.add(inst);
    return next;
  });

  const enviar = async () => {
    setEnviando(true);
    const ok = await onSubmit(elegidos.map((r) => r.instance));
    setEnviando(false);
    if (ok) onClose();
  };

  return (
    <div className="modal-overlay" onClick={enviando ? undefined : onClose}>
      <div className="modal-content dist-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-header-text">
            <h3 className="modal-title">Asignación masiva a todas las aplicaciones</h3>
            <p className="dist-modal-recurso">Recursos sin asignar de bajo costo, repartidos en partes iguales entre todas las aplicaciones</p>
          </div>
          <button className="modal-close" onClick={onClose} aria-label="Cerrar" disabled={enviando}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="modal-body consumo-form">
          <div className="consumo-campo">
            <span>Recursos sin asignar de menos de</span>
            <div className="dist-umbral">
              <div className="dist-patron dist-umbral-input">
                <span className="dist-patron-pre">USD</span>
                <input
                  type="number"
                  min="0"
                  step="0.5"
                  value={umbral}
                  onChange={(e) => { setUmbral(e.target.value); setExcluidos(new Set()); }}
                />
              </div>
              <div className="dist-modo dist-umbral-chips" role="group" aria-label="Montos rápidos">
                {UMBRALES.map((u) => (
                  <button
                    key={u}
                    type="button"
                    className={`dist-modo-opt ${limite === u ? 'activo' : ''}`}
                    onClick={() => { setUmbral(String(u)); setExcluidos(new Set()); }}
                  >
                    ${u}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="dist-coinciden">
            <div className="dist-coinciden-head">
              <input
                type="checkbox"
                checked={todosMarcados}
                ref={(el) => { if (el) el.indeterminate = elegidos.length > 0 && !todosMarcados; }}
                disabled={candidatos.length === 0}
                onChange={() => setExcluidos(todosMarcados ? new Set(candidatos.map((r) => r.instance)) : new Set())}
                aria-label="Seleccionar todos"
              />
              <span className={`dist-coinciden-num ${elegidos.length ? '' : 'cero'}`}>{elegidos.length}</span>
              <span>
                {elegidos.length === 1 ? 'recurso seleccionado' : 'recursos seleccionados'}
                {candidatos.length !== elegidos.length && <small> de {candidatos.length}</small>}
              </span>
              <span className="dist-lote-total">{formatCurrency(totalUsd)}</span>
            </div>
            {candidatos.length > 0 ? (
              <ul className="dist-preview dist-lote-lista">
                {candidatos.map((r) => (
                  <li key={`${r.service}|${r.instance}`}>
                    <label className="dist-lote-item">
                      <input type="checkbox" checked={!excluidos.has(r.instance)} onChange={() => toggle(r.instance)} />
                      <span className="dist-preview-nombre" title={`${r.instance} · ${r.service}`}>{r.instance}</span>
                    </label>
                    <span className="dist-preview-usd">{formatCurrency(r.costo_usd)}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="dist-lote-vacio">No hay recursos sin asignar por debajo de ese monto.</p>
            )}
          </div>

          <p className="consumo-ayuda">
            Se crea una regla exacta por recurso hacia <strong>todas las aplicaciones</strong> (incluidas las que
            se agreguen después). Si un recurso cambia de nombre en otro mes, aparecerá de nuevo como sin asignar.
          </p>
        </div>

        <div className="modal-footer">
          <button className="modal-btn modal-btn-ghost" onClick={onClose} disabled={enviando}>Cancelar</button>
          <button className="modal-btn modal-btn-primary" disabled={elegidos.length === 0 || enviando} onClick={enviar}>
            {enviando ? 'Asignando…' : `Asignar ${elegidos.length} recurso${elegidos.length === 1 ? '' : 's'}`}
          </button>
        </div>
      </div>
    </div>
  );
};

/**
 * Módulo Distribución servicios (submenú de Costos Cloud, 2026-10). Asigna
 * cada recurso del CSV de IBM Cloud a la aplicación (o aplicaciones) que lo
 * usa, mediante reglas que valen para todos los meses: exactas o "contiene",
 * para los recursos que cambian de nombre (workers de Kubernetes, snapshots).
 */
export const DistribucionModule = ({ showNotification }) => {
  const { cortes, loadCortes } = useCortes(showNotification);
  const { apps, reglas, recursos, loading, loadBase, loadRecursos, guardarRegla, eliminarRegla, asignarLote } =
    useDistribucion(showNotification);

  const [periodo, setPeriodo] = useState('');
  const [vista, setVista] = useState('recursos');
  const [filtro, setFiltro] = useState('sin');
  const [busqueda, setBusqueda] = useState('');
  const [modal, setModal] = useState(null); // { recurso?, regla? }
  const [confirm, setConfirm] = useState(null);
  const [lote, setLote] = useState(false);

  useEffect(() => {
    loadBase();
    loadCortes().then((lista) => {
      if (lista.length) setPeriodo((p) => p || lista[lista.length - 1].periodo);
    });
  }, [loadBase, loadCortes]);

  useEffect(() => {
    loadRecursos(periodo);
  }, [periodo, loadRecursos]);

  const lista = useMemo(
    () => (recursos?.periodo === periodo ? recursos.recursos : []),
    [recursos, periodo]
  );
  const reglaPorId = useMemo(() => new Map(reglas.map((r) => [r.id, r])), [reglas]);
  const total = lista.reduce((s, r) => s + r.costo_usd, 0);
  const asignados = lista.filter((r) => r.regla_id);
  const totalAsignado = asignados.reduce((s, r) => s + r.costo_usd, 0);
  const pctCosto = total ? (totalAsignado / total) * 100 : 0;
  const idx = cortes.findIndex((c) => c.periodo === periodo);

  const q = busqueda.trim().toLowerCase();
  const visibles = lista.filter((r) => {
    if (filtro === 'sin' && r.regla_id) return false;
    if (filtro === 'con' && !r.regla_id) return false;
    return !q || r.instance.toLowerCase().includes(q) || r.service.toLowerCase().includes(q);
  });

  // Recursos del mes que cubre cada regla (para la pestaña Reglas).
  const usoReglas = useMemo(() => {
    const m = new Map();
    lista.forEach((r) => {
      if (!r.regla_id) return;
      const u = m.get(r.regla_id) || { n: 0, usd: 0 };
      u.n += 1;
      u.usd += r.costo_usd;
      m.set(r.regla_id, u);
    });
    return m;
  }, [lista]);

  const reglasVisibles = reglas.filter((r) => !q || r.patron.toLowerCase().includes(q) || (r.servicio || '').toLowerCase().includes(q));

  const recargar = async () => {
    await loadBase();
    await loadRecursos(periodo);
  };

  const guardar = async (id, regla) => {
    const ok = await guardarRegla(id, regla);
    if (ok) {
      showNotification?.('success', 'Distribución', id ? 'Asignación actualizada' : 'Asignación guardada');
      recargar();
    }
    return ok;
  };

  const pedirEliminar = (regla) => setConfirm({
    title: 'Quitar asignación',
    message: `¿Eliminar la regla ${describirRegla(regla)}? Sus recursos quedan sin asignar (salvo que otra regla los cubra).`,
    confirmLabel: 'Eliminar',
    onConfirm: async () => {
      setConfirm(null);
      if (await eliminarRegla(regla.id)) {
        showNotification?.('success', 'Distribución', 'Regla eliminada');
        setModal(null);
        recargar();
      }
    }
  });

  return (
    <div className="module-proceso">
      <div className="module-header">
        <h1>Distribución servicios</h1>
        <p>Asigna cada recurso de IBM Cloud a la aplicación que lo usa. Las asignaciones valen para todos los meses.</p>
      </div>

      {cortes.length === 0 ? (
        <div className="consumo-vacio">
          <h2>No hay meses cargados</h2>
          <p>Carga un CSV en Costos Cloud → Consumo mensual para ver sus recursos aquí.</p>
        </div>
      ) : (
        <>
          <div className="consumo-toolbar">
            <div className="consumo-nav">
              <button type="button" className="consumo-nav-btn" disabled={idx <= 0} onClick={() => setPeriodo(cortes[idx - 1].periodo)} aria-label="Mes anterior">‹</button>
              <select className="consumo-nav-select" value={periodo} onChange={(e) => setPeriodo(e.target.value)} aria-label="Mes">
                {cortes.map((c) => <option key={c.periodo} value={c.periodo}>{nombreMes(c.periodo)}</option>)}
              </select>
              <button type="button" className="consumo-nav-btn" disabled={idx === -1 || idx >= cortes.length - 1} onClick={() => setPeriodo(cortes[idx + 1].periodo)} aria-label="Mes siguiente">›</button>
            </div>
            <div className="dist-progreso" title={`${formatCurrency(totalAsignado)} de ${formatCurrency(total)} asignados`}>
              <div className="dist-progreso-txt">
                <strong>{asignados.length}</strong> de {lista.length} recursos asignados · {pctCosto.toFixed(1)}% del costo
                {lista.length > asignados.length && (
                  <span className="dist-progreso-sin"> · sin asignar {formatCurrency(total - totalAsignado)}</span>
                )}
              </div>
              <div className="dist-progreso-barra"><span style={{ width: `${pctCosto}%` }} /></div>
            </div>
          </div>

          <div className="consumo-tabs">
            <button type="button" className={`consumo-tab ${vista === 'recursos' ? 'activa' : ''}`} onClick={() => setVista('recursos')}>
              Recursos <span className="consumo-cuenta">{lista.length}</span>
            </button>
            <button type="button" className={`consumo-tab ${vista === 'reglas' ? 'activa' : ''}`} onClick={() => setVista('reglas')}>
              Reglas <span className="consumo-cuenta">{reglas.length}</span>
            </button>
            {vista === 'recursos' && (
              <div className="dist-filtros" role="group" aria-label="Filtrar recursos">
                {[['sin', 'Sin asignar'], ['con', 'Asignados'], ['todos', 'Todos']].map(([id, label]) => (
                  <button key={id} type="button" className={`dist-filtro ${filtro === id ? 'activo' : ''}`} onClick={() => setFiltro(id)}>
                    {label}
                  </button>
                ))}
              </div>
            )}
            {vista === 'recursos' && lista.length > asignados.length && (
              <button type="button" className="dist-lote-btn" onClick={() => setLote(true)}>
                Asignar menores a…
              </button>
            )}
            <input
              className="proceso-input consumo-buscar"
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
              placeholder={vista === 'recursos' ? 'Buscar recurso o servicio' : 'Buscar regla'}
            />
          </div>

          {vista === 'recursos' ? (
            <div className="consumo-tabla-wrap">
              <table className="consumo-tabla dist-tabla">
                <thead>
                  <tr>
                    <th>Recurso</th>
                    <th className="num">USD</th>
                    <th>Aplicación</th>
                  </tr>
                </thead>
                <tbody>
                  {loading && lista.length === 0 && <tr><td colSpan={3} className="consumo-tabla-vacia">Cargando…</td></tr>}
                  {!loading && visibles.length === 0 && (
                    <tr>
                      <td colSpan={3} className="consumo-tabla-vacia">
                        {filtro === 'sin' && !q ? '¡Todos los recursos de este mes están asignados!' : 'Nada que mostrar'}
                      </td>
                    </tr>
                  )}
                  {visibles.map((r) => {
                    const regla = r.regla_id ? reglaPorId.get(r.regla_id) : null;
                    return (
                      <tr
                        key={`${r.service}|${r.instance}`}
                        className="dist-fila"
                        onClick={() => setModal({ recurso: r, regla })}
                      >
                        <td>
                          <span className="dist-recurso">{r.instance || '(sin nombre)'}</span>
                          <span className="dist-servicio">{r.service}</span>
                        </td>
                        <td className="num">{formatCurrency(r.costo_usd)}</td>
                        <td>
                          {regla ? (
                            <>
                              <AppsDeRegla regla={regla} apps={apps} />
                              {regla.tipo !== 'exacto' && (
                                <span className="dist-por-regla" title={describirRegla(regla)}>por regla «{regla.patron}»</span>
                              )}
                            </>
                          ) : (
                            <span className="dist-sin">Sin asignar</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="consumo-tabla-wrap">
              <table className="consumo-tabla dist-tabla">
                <thead>
                  <tr>
                    <th>Regla</th>
                    <th>Aplicación</th>
                    <th className="num">Recursos en {periodo ? nombreMes(periodo) : 'el mes'}</th>
                    <th aria-label="Acciones" />
                  </tr>
                </thead>
                <tbody>
                  {reglasVisibles.length === 0 && (
                    <tr><td colSpan={4} className="consumo-tabla-vacia">Aún no hay reglas. Asigna un recurso desde la pestaña Recursos.</td></tr>
                  )}
                  {reglasVisibles.map((r) => {
                    const uso = usoReglas.get(r.id);
                    return (
                      <tr key={r.id}>
                        <td>
                          <span className={`dist-tipo dist-tipo-${r.tipo}`}>{tipoDe(r.tipo).corto}</span>
                          <span className="dist-recurso">{r.patron}</span>
                          {r.servicio && <span className="dist-servicio">solo {r.servicio}</span>}
                        </td>
                        <td><AppsDeRegla regla={r} apps={apps} /></td>
                        <td className="num">
                          {uso ? `${uso.n} · ${formatCurrency(uso.usd)}` : <span className="consumo-ayuda">ninguno</span>}
                        </td>
                        <td className="contrato-fila-menu">
                          <MenuAcciones
                            label={`Acciones de la regla ${r.patron}`}
                            acciones={[
                              { label: 'Editar', onClick: () => setModal({ regla: r }) },
                              { label: 'Eliminar', danger: true, onClick: () => pedirEliminar(r) }
                            ]}
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {modal && (
        <ReglaModal
          key={`${modal.recurso?.instance || ''}|${modal.regla?.id || ''}`}
          recurso={modal.recurso}
          regla={modal.regla}
          apps={apps}
          recursosMes={lista}
          onClose={() => setModal(null)}
          onSave={guardar}
          onDelete={pedirEliminar}
        />
      )}

      {lote && (
        <LoteModal
          recursos={lista}
          onClose={() => setLote(false)}
          onSubmit={async (instancias) => {
            const r = await asignarLote(instancias);
            if (!r) return false;
            showNotification?.(
              'success',
              'Distribución',
              `${r.creadas} recurso${r.creadas === 1 ? '' : 's'} asignado${r.creadas === 1 ? '' : 's'} a todas las aplicaciones${r.omitidas ? ` (${r.omitidas} ya tenían regla)` : ''}`
            );
            recargar();
            return true;
          }}
        />
      )}

      {confirm && (
        <ConfirmModal
          title={confirm.title}
          message={confirm.message}
          confirmLabel={confirm.confirmLabel}
          onConfirm={confirm.onConfirm}
          onClose={() => setConfirm(null)}
        />
      )}
    </div>
  );
};
